import type { MatchStatus } from "service";
import { normalize, parseSubInfo } from "./helper";

export type LineupProps = { status: MatchStatus };

export type TeamRoster = {
  starting: {
    number: number;
    name: string;
    position?: string;
    subInfo?: { dir: "IN" | "OUT"; minute: number } | null;
    goals?: number;
    yellow?: number;
    red?: number;
  }[];
  substitutes: {
    number: number;
    name: string;
    position?: string;
    subInfo?: { dir: "IN" | "OUT"; minute: number } | null;
    goals?: number;
    yellow?: number;
    red?: number;
  }[];
};

// Helper to count delimited values (e.g., "12,45" => 2, "" or null => 0)
function countFromDelimited(val: string | number | undefined | null): number {
  if (val == null) return 0;
  if (typeof val === "number") return Number.isFinite(val) ? 1 : 0;
  if (typeof val === "string") {
    const s = val.trim();
    if (!s) return 0;
    return s.split(",").filter(Boolean).length;
  }
  return 0;
}

export const buildRosterFromPly = (
  sideObj: any,
  order: { starting: string[]; subs: string[] }
): TeamRoster => {
  const roster: TeamRoster = { starting: [], substitutes: [] };
  const ply: Record<string, any> = sideObj?.ply || {};

  const allPlayers: any[] = Object.values(ply || {});
  const usedByRef = new Set<any>();
  const usedByName = new Set<string>();

  const nameMap = new Map<string, any>();
  Object.values(ply).forEach((p: any) => {
    const candidateNames = (
      [
        p?.n,
        `${(p?.n2 || "").trim()} ${(p?.n1 || "").trim()}`.trim(),
        (p?.n1 || "").trim(),
        (p?.n2 || "").trim(),
      ] as string[]
    )
      .map(normalize)
      .filter(Boolean);

    const unique = Array.from(new Set(candidateNames));
    unique.forEach((key) => {
      if (!nameMap.has(key)) nameMap.set(key, p);
    });
  });

  const toRow = (p: any, isSubList: boolean) => {
    const name =
      p?.n || `${(p?.n2 || "").trim()} ${(p?.n1 || "").trim()}`.trim() || "-";
    const numRaw = p?.no ?? p?.num ?? p?.shirt;
    const number = Number.isFinite(Number(numRaw)) ? Number(numRaw) : 0;
    const position = p?.pos ?? p?.position ?? p?.role ?? undefined;
    const isSubApi = !!p?.sub;
    const subInfo = parseSubInfo(p?.subs, isSubList || isSubApi);
    const goals = countFromDelimited(p?.goal);
    const yellow = countFromDelimited(p?.ycard);
    const red = countFromDelimited(p?.rcard);
    return { number, name, position, subInfo, goals, yellow, red };
  };

  const safePush = (
    target: "starting" | "substitutes",
    p: any,
    isSubList: boolean
  ) => {
    if (!p) return;
    if (usedByRef.has(p)) return;
    const nm = normalize(
      p?.n || `${(p?.n2 || "").trim()} ${(p?.n1 || "").trim()}`.trim()
    );
    if (!nm) return;
    if (usedByName.has(nm)) return;

    usedByRef.add(p);
    usedByName.add(nm);

    if (target === "starting") roster.starting.push(toRow(p, isSubList));
    else roster.substitutes.push(toRow(p, isSubList));
  };

  order.starting.forEach((rawName) => {
    const key = normalize(rawName);
    const p = nameMap.get(key);
    if (p) safePush("starting", p, false);
  });

  order.subs.forEach((rawName) => {
    const key = normalize(rawName);
    const p = nameMap.get(key);
    if (p) safePush("substitutes", p, true);
  });

  allPlayers.forEach((p) => {
    if (usedByRef.has(p)) return;
    if (p?.sub) safePush("substitutes", p, true);
    else safePush("starting", p, false);
  });

  const seen = new Set<string>();
  roster.starting = roster.starting.filter((row) => {
    const k = normalize(row.name);
    if (!k) return false;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
  roster.substitutes = roster.substitutes.filter((row) => {
    const k = normalize(row.name);
    if (!k) return false;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });

  return roster;
};
